<template>
    <component
        :is="floating ? 'div' : MountedTeleport"
        v-if="floating || isMulti || (selectedBlock && selectedId)"
        :to="floating ? undefined : '#aside'"
    >
        <div v-if="isMulti" class="pb2-panel" :class="{ 'pb2-panel--floating': floating }">
            <div class="pb2-panel__crumb">
                <span class="pb2-panel__crumb-current">
                    {{ t('selected_count', { count: store.selectedIds.value.length }) }}
                </span>
                <button
                    v-tooltip="t('close_panel')"
                    type="button"
                    class="pb2-panel__close"
                    :aria-label="t('close_panel')"
                    @click="store.select(null)"
                >
                    ×
                </button>
            </div>

            <div class="pb2-panel__actions">
                <button
                    v-tooltip="t('duplicate')"
                    type="button"
                    class="pb2-panel__action"
                    :aria-label="t('duplicate')"
                    @click="store.duplicateBlocks(selection())"
                >
                    <Icon icon="phosphor/regular/copy" class="size-3.5" />
                </button>
                <button
                    v-tooltip="t('wrap_in_section')"
                    type="button"
                    class="pb2-panel__action"
                    :disabled="!wrapEligible"
                    :aria-label="t('wrap_in_section')"
                    @click="store.wrapInSection(selection())"
                >
                    <Icon icon="phosphor/regular/frame-corners" class="size-3.5" />
                </button>
                <button
                    v-tooltip="t('delete')"
                    type="button"
                    class="pb2-panel__action pb2-panel__action--danger"
                    :aria-label="t('delete')"
                    @click="store.removeBlocks(selection())"
                >
                    <Icon icon="phosphor/regular/trash" class="size-3.5" />
                </button>
            </div>
        </div>

        <div
            v-else-if="selectedBlock && selectedId"
            ref="panelEl"
            class="pb2-panel"
            :class="{ 'pb2-panel--floating': floating }"
        >
            <div class="pb2-panel__crumb">
                <button type="button" class="pb2-panel__crumb-link" @click="store.select(null)">
                    {{ t('page_crumb') }}
                </button>
                <template v-for="ancestor in ancestors" :key="ancestor.id">
                    <span class="pb2-panel__crumb-sep">‹</span>
                    <button type="button" class="pb2-panel__crumb-link" @click="store.select(ancestor.id)">
                        {{ ancestor.label }}
                    </button>
                </template>
                <span class="pb2-panel__crumb-sep">‹</span>
                <span class="pb2-panel__crumb-current">{{ label }}</span>
                <button
                    v-tooltip="t('close_panel')"
                    type="button"
                    class="pb2-panel__close"
                    :aria-label="t('close_panel')"
                    @click="store.select(null)"
                >
                    ×
                </button>
            </div>

            <div class="pb2-panel__sections">
                <p v-if="isSharedBlock(selectedBlock)" class="pb2-panel__shared-note">
                    {{ t('shared_block_note') }}
                </p>

                <template v-else>
                    <SettingsSection
                        v-if="generalAttributes.length"
                        v-model="segments.general"
                        :title="t('general_segment')"
                    >
                        <div v-for="attribute in generalAttributes" :key="attribute.slug" class="pb2-panel__field">
                            <Field
                                :model-value="selectedBlock.value?.[attribute.slug]"
                                :field="attribute"
                                size="xs"
                                label-position="top"
                                :searchable="false"
                                @update:model-value="updateAttribute(attribute.slug, $event)"
                            />
                        </div>
                    </SettingsSection>

                    <SettingsSection v-if="cssAttributes.length" v-model="segments.css" :title="t('css_segment')">
                        <div v-for="attribute in cssAttributes" :key="attribute.slug" class="pb2-panel__field">
                            <Field
                                :model-value="selectedBlock.value?.[attribute.slug]"
                                :field="attribute"
                                size="xs"
                                label-position="top"
                                :searchable="false"
                                @update:model-value="updateAttribute(attribute.slug, $event)"
                            />
                        </div>
                    </SettingsSection>
                </template>

                <SettingsSection v-model="segments.visibility" :title="t('visibility')">
                    <span class="pb2-panel__visibility-toggles">
                        <button
                            v-for="option in visibilityOptions"
                            :key="option.key"
                            v-tooltip="
                                hiddenOn(option.key)
                                    ? t('visibility_show_on', { breakpoint: option.label })
                                    : t('visibility_hide_on', { breakpoint: option.label })
                            "
                            type="button"
                            class="pb2-panel__visibility-toggle"
                            :class="{ 'pb2-panel__visibility-toggle--hidden': hiddenOn(option.key) }"
                            :aria-pressed="!hiddenOn(option.key)"
                            :aria-label="
                                hiddenOn(option.key)
                                    ? t('visibility_show_on', { breakpoint: option.label })
                                    : t('visibility_hide_on', { breakpoint: option.label })
                            "
                            @click="store.setVisibility(selectedId, option.key, hiddenOn(option.key))"
                        >
                            <Icon :icon="option.icon" class="size-3.5" />
                        </button>
                    </span>
                </SettingsSection>

                <div class="pb2-panel__actions">
                    <button
                        v-tooltip="t('move_up')"
                        type="button"
                        class="pb2-panel__action"
                        :aria-label="t('move_up')"
                        @click="store.moveBlock(selectedId, -1)"
                    >
                        <Icon icon="phosphor/regular/arrow-up" class="size-3.5" />
                    </button>
                    <button
                        v-tooltip="t('move_down')"
                        type="button"
                        class="pb2-panel__action"
                        :aria-label="t('move_down')"
                        @click="store.moveBlock(selectedId, 1)"
                    >
                        <Icon icon="phosphor/regular/arrow-down" class="size-3.5" />
                    </button>
                    <button
                        v-tooltip="t('duplicate')"
                        type="button"
                        class="pb2-panel__action"
                        :aria-label="t('duplicate')"
                        @click="store.duplicateBlock(selectedId)"
                    >
                        <Icon icon="phosphor/regular/copy" class="size-3.5" />
                    </button>
                    <button
                        v-if="wrapEligible"
                        v-tooltip="t('wrap_in_section')"
                        type="button"
                        class="pb2-panel__action"
                        :aria-label="t('wrap_in_section')"
                        @click="store.wrapInSection(selection())"
                    >
                        <Icon icon="phosphor/regular/frame-corners" class="size-3.5" />
                    </button>
                    <button
                        v-tooltip="t('delete')"
                        type="button"
                        class="pb2-panel__action pb2-panel__action--danger"
                        :aria-label="t('delete')"
                        @click="store.removeBlock(selectedId)"
                    >
                        <Icon icon="phosphor/regular/trash" class="size-3.5" />
                    </button>
                </div>
            </div>
        </div>

        <p v-else class="pb2-panel__empty">
            {{ t('panel_empty') }}
        </p>
    </component>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useBuilderT } from '@src/Common/Builder/host';
import Field from '@src/Common/Components/Field.vue';
import MountedTeleport from '@src/Common/Components/MountedTeleport.vue';
import SettingsSection from '@src/Common/Builder/Panel/SettingsSection.vue';
import { canWrapInSection } from '@src/Common/Builder/nestingRules';
import { isSharedBlock, markerId } from '@src/Common/Builder/types';
import { useBlockAttributes } from '@src/Common/Builder/Panel/useBlockAttributes';
import type { BlockNode, BuilderBreakpoint } from '@src/Common/Builder/types';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';

const t = useBuilderT();

/** Floating (F2 panel system) renders bare content incl. an empty state;
 *  without it the panel keeps the docked #aside teleport (Canvas-lite). */
const props = withDefaults(defineProps<{ floating?: boolean }>(), { floating: false });
const floating = computed(() => props.floating);

const store = useBuilderStore();

const selectedId = computed(() => store.selectedId.value);
const selectedBlock = computed(() => (selectedId.value ? store.findBlock(selectedId.value) : null));
const isMulti = computed(() => store.selectedIds.value.length > 1);

const selection = (): string[] => [...store.selectedIds.value];
const wrapEligible = computed(() => canWrapInSection(store.content.value, store.selectedIds.value));

const label = computed(() =>
    selectedId.value ? (store.labels.value[selectedId.value] ?? String(selectedBlock.value?.type ?? '')) : '',
);

/** Container ancestors of the selection, root-first (crumb segments). */
const ancestors = computed<Array<{ id: string; label: string }>>(() => {
    const target = selectedId.value;

    if (target === null) {
        return [];
    }

    let chain: Array<{ id: string; label: string }> = [];

    const visit = (blocks: BlockNode[], trail: Array<{ id: string; label: string }>): boolean => {
        for (const block of blocks) {
            const id = markerId(block);

            if (id === target) {
                chain = trail;
                return true;
            }

            if (block.children?.length && id !== null) {
                const step = { id, label: store.labels.value[id] ?? String(block.type) };

                if (visit(block.children, [...trail, step])) {
                    return true;
                }
            }
        }

        return false;
    };

    visit(store.content.value, []);

    return chain;
});

const { generalAttributes, cssAttributes, updateAttribute } = useBlockAttributes(() => selectedBlock.value);

const panelEl = ref<HTMLElement | null>(null);

// Dblclick on a block without inline editing (rich text, shared block)
// routes here — focus the first editable field as the §4.2 hand-off.
watch(store.panelFocusSerial, async () => {
    await nextTick();
    const first = panelEl.value?.querySelector<HTMLElement>('input, textarea, select, [contenteditable="true"]');
    first?.focus();
});

// F4: hairline segments — Obecné + Viditelnost open, CSS collapsed
const segments = ref({ general: true, css: false, visibility: true });

// Applies to shared blocks too — visibility is a level-root key, so the
// segment lives outside the type-attributes branch.
const visibilityOptions = computed<{ key: BuilderBreakpoint; label: string; icon: string }[]>(() => [
    { key: 'desktop', label: t('breakpoint_desktop'), icon: 'phosphor/regular/desktop' },
    { key: 'tablet', label: t('breakpoint_tablet'), icon: 'phosphor/regular/device-tablet' },
    { key: 'mobile', label: t('breakpoint_mobile'), icon: 'phosphor/regular/device-mobile' },
]);

const hiddenOn = (breakpoint: BuilderBreakpoint): boolean => selectedBlock.value?.visibility?.[breakpoint] === false;
</script>

<style scoped>
.pb2-panel {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding: 0.75rem;
    background: var(--pb2-surface, #fafafa);
    border: 1px solid var(--pb2-border, #e4e4e7);
    border-radius: var(--pb2-radius, 6px);
}

/* Collapsible sections are hairline-divided (own border-top), so they sit
 * flush — the panel gap must not add space around a collapsed section's head. */
.pb2-panel__sections {
    display: flex;
    flex-direction: column;
}

/* Inside a FloatingPanel the chrome (surface, border, title) is the host's. */
.pb2-panel--floating {
    padding: 0.25rem;
    background: none;
    border: 0;
    border-radius: 0;
}

.pb2-panel__empty {
    margin: 0;
    padding: 1rem 0.5rem;
    font-size: 0.75rem;
    text-align: center;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-panel__crumb {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-panel__crumb-link {
    background: none;
    border: 0;
    padding: 0;
    font-size: 0.75rem;
    color: var(--pb2-accent, #2563eb);
    cursor: pointer;
}

.pb2-panel__crumb-current {
    font-weight: 600;
    color: var(--pb2-text, #3f3f46);
}

.pb2-panel__close {
    margin-left: auto;
    background: none;
    border: 0;
    font-size: 1rem;
    line-height: 1;
    color: var(--pb2-muted, #a1a1aa);
    cursor: pointer;
}

.pb2-panel__shared-note {
    margin: 0;
    font-size: 0.75rem;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-panel__visibility-toggles {
    display: flex;
    gap: 0.3125rem;
}

.pb2-panel__visibility-toggle {
    display: grid;
    place-items: center;
    flex: 1;
    height: 24px;
    color: var(--pb2-text, #3f3f46);
    background: none;
    border: 1px solid var(--pb2-border, #e4e4e7);
    border-radius: 0;
    cursor: pointer;
    transition:
        color 140ms ease,
        opacity 140ms ease;
}

.pb2-panel__visibility-toggle:hover {
    color: var(--pb2-text, #3f3f46);
    border-color: var(--pb2-muted, #a1a1aa);
}

.pb2-panel__visibility-toggle--hidden {
    color: var(--pb2-muted, #a1a1aa);
    opacity: 0.4;
}

.pb2-panel__actions {
    display: flex;
    gap: 0;
    padding-top: 0.5rem;
    border-top: 1px solid var(--pb2-border, #e4e4e7);
}

/* Icon buttons — same language as .pb2-toolbar__button */
.pb2-panel__action {
    display: grid;
    place-items: center;
    width: 1.5rem;
    height: 1.5rem;
    color: var(--pb2-muted, #a1a1aa);
    background: transparent;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    transition: color 140ms ease;
}

.pb2-panel__action:hover {
    color: var(--pb2-text, #3f3f46);
}

.pb2-panel__action--danger:hover {
    color: var(--pb2-danger, #dc2626);
}

.pb2-panel__action:disabled {
    opacity: 0.4;
    cursor: default;
}

.pb2-panel__action:disabled:hover {
    color: var(--pb2-muted, #a1a1aa);
}

@media (prefers-reduced-motion: reduce) {
    .pb2-panel__visibility-toggle,
    .pb2-panel__action {
        transition: none;
    }
}
</style>
